"use client";

import { motion, useReducedMotion } from "framer-motion";
import {
  AlarmClock,
  ArrowRight,
  BellRingIcon,
  CalendarClock,
  Check,
  ListTodo,
  Smartphone,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";

const EASE = [0.16, 1, 0.3, 1] as const;

const CAPABILITIES = [
  { icon: ListTodo, label: "Simple but powerful" },
  { icon: BellRingIcon, label: "Notifications" },
  { icon: AlarmClock, label: "Smart snooze" },
  { icon: Smartphone, label: "Work from anywhere" },
];

// Keep the supporting board readable on the left, dissolving only where the
// crisp task composition pops over its right edge.
const BOARD_FADE = {
  WebkitMaskImage: "linear-gradient(to right, black 0%, black 60%, transparent 100%)",
  maskImage: "linear-gradient(to right, black 0%, black 60%, transparent 100%)",
} as const;

/* ------------------------------------------------------------------ */
/* Hero copy                                                          */
/* ------------------------------------------------------------------ */

function HeroCopy() {
  const reduceMotion = useReducedMotion();
  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
  };
  const containerProps = reduceMotion
    ? {}
    : { initial: "hidden" as const, animate: "visible" as const, variants: container };
  const iv = reduceMotion ? undefined : item;

  return (
    <motion.div
      className="flex max-w-xl flex-col items-center text-center xl:items-start xl:text-left"
      {...containerProps}
    >
      <motion.span
        variants={iv}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
      >
        <ListTodo className="size-4" />
        Tasks
      </motion.span>

      <motion.h1
        variants={iv}
        className="mt-4 text-balance text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-[2.75rem] lg:text-5xl"
      >
        Follow-ups that actually get done
      </motion.h1>

      <motion.p
        variants={iv}
        className="mt-6 max-w-lg text-balance leading-relaxed text-muted-foreground sm:text-lg"
      >
        Turn &ldquo;I&rsquo;ll call you tomorrow&rdquo; into a task that&rsquo;s linked to the
        contact, handed to a teammate, and checked off on time. Leave nothing to memory.
      </motion.p>

      <motion.div variants={iv} className="mt-8 w-full sm:w-auto">
        <Button asChild size="xl" className="group w-full sm:w-auto">
          <Link href="/book-a-demo">
            Book a demo
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </Button>
      </motion.div>

      <motion.div
        variants={iv}
        className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 xl:justify-start"
      >
        {CAPABILITIES.map(({ icon: Icon, label }) => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
          >
            <Icon className="size-3.5" />
            {label}
          </span>
        ))}
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Supporting board (faded, behind)                                   */
/* ------------------------------------------------------------------ */

type BoardTask = { title: string; due: string; overdue?: boolean };

const BOARD: { column: string; count: number; tasks: BoardTask[] }[] = [
  {
    column: "To do",
    count: 4,
    tasks: [
      { title: "Follow up on trial week", due: "Yesterday", overdue: true },
      { title: "Confirm Saturday's private session", due: "Today" },
    ],
  },
  {
    column: "In progress",
    count: 2,
    tasks: [
      { title: "Rebook Chris after his freeze", due: "Today" },
      { title: "Waitlist outreach for 6am HIIT", due: "Tomorrow" },
    ],
  },
];

function BoardColumn({
  column,
  count,
  tasks,
}: {
  column: string;
  count: number;
  tasks: BoardTask[];
}) {
  return (
    <div className="w-[200px] shrink-0">
      <div className="flex items-center gap-2 px-1">
        <span className="text-[12px] font-semibold text-foreground">{column}</span>
        <span className="rounded-full bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground">
          {count}
        </span>
      </div>
      <div className="mt-2 space-y-2">
        {tasks.map((task) => (
          <div
            key={task.title}
            className="rounded-lg border border-border bg-background p-2.5 shadow-sm"
          >
            <div className="flex items-start gap-2">
              <span className="mt-0.5 size-3.5 shrink-0 rounded-full border-[1.5px] border-border" />
              <p className="text-[12px] font-medium leading-snug text-foreground">{task.title}</p>
            </div>
            <p
              className={cn(
                "mt-1.5 pl-[22px] text-[10px]",
                task.overdue
                  ? "font-medium text-red-500 dark:text-red-400"
                  : "text-muted-foreground",
              )}
            >
              {task.overdue ? "Overdue · " : "Due "}
              {task.due}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function BoardMock({ className }: { className?: string }) {
  return (
    <div className={cn("rounded-2xl border border-border bg-muted/30 p-4", className)}>
      <div className="flex gap-4">
        {BOARD.map((col) => (
          <BoardColumn key={col.column} {...col} />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Crisp task composition (front)                                     */
/* ------------------------------------------------------------------ */

function AssignedPush() {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border bg-background p-2.5 shadow-md">
      <Image
        src="/images/icon.png"
        width={32}
        height={32}
        alt=""
        className="size-7 shrink-0 rounded-[7px]"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12px] font-semibold text-foreground">
          You were assigned a task
        </p>
        <p className="truncate text-[11px] text-muted-foreground">
          Call Maya about her intro offer
        </p>
      </div>
      <span className="shrink-0 text-[10px] text-muted-foreground">now</span>
    </div>
  );
}

/**
 * The card's checkbox ticks itself after the composition settles: the loop
 * closes on screen the way the copy promises. Reduced motion renders the
 * completed end-state immediately.
 */
function CompletingCheckbox() {
  const reduceMotion = useReducedMotion();
  return (
    <span className="relative mt-0.5 size-[18px] shrink-0">
      <span className="absolute inset-0 rounded-full border-2 border-border" />
      <motion.span
        className="absolute inset-0 flex items-center justify-center rounded-full bg-green-600 dark:bg-green-500"
        initial={reduceMotion ? false : { opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: EASE, delay: 2.2 }}
      >
        <Check className="size-3 text-white" strokeWidth={3.5} />
      </motion.span>
    </span>
  );
}

function TaskCard() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-xl">
      <div className="p-4">
        <div className="flex items-start gap-2.5">
          <CompletingCheckbox />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-snug text-foreground">
              Call Maya about her intro offer
            </p>
            <p className="mt-1 text-[12px] leading-snug text-muted-foreground">
              Intro month ends Friday. Offer the founding rate before it lapses.
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5 pl-[28px]">
          <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-foreground">
            <CalendarClock className="size-3 text-muted-foreground" />
            Today · 3:00 PM
          </span>
          <span className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            follow-up
          </span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Avatar name="Maya Rivera" src="/images/website/customers/maya-rivera.jpg" size="xs" />
          Maya Rivera
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          Assigned to
          <Avatar name="Alex Romero" size="xs" />
          <span className="font-medium text-foreground">Alex</span>
        </span>
      </div>
    </div>
  );
}

function Stage() {
  const reduceMotion = useReducedMotion();
  const boardAnim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: -16 },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.8, ease: EASE, delay: 0.3 },
      };
  const cardAnim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: 24, scale: 0.98 },
        animate: { opacity: 1, x: 0, scale: 1 },
        transition: { duration: 0.8, ease: EASE, delay: 0.45 },
      };
  const pushAnim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: -12 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, ease: EASE, delay: 0.85 },
      };

  return (
    // The task composition sits in flow and defines the height; the board is
    // layered behind it so the section always contains the composition.
    <div className="relative w-full">
      {/* Supporting task board, readable base, dissolving under the card */}
      <motion.div
        aria-hidden
        {...boardAnim}
        className="absolute left-0 top-1/2 hidden w-[440px] -translate-y-1/2 xl:block"
        style={BOARD_FADE}
      >
        <BoardMock className="opacity-70" />
      </motion.div>

      {/* The assigned task, crisp, popped in front */}
      <div className="relative z-10 mx-auto w-[300px] sm:w-[332px] xl:ml-auto xl:mr-0">
        <motion.div {...pushAnim} className="relative z-10 mx-4 mb-3">
          <AssignedPush />
        </motion.div>
        <motion.div {...cardAnim}>
          <TaskCard />
        </motion.div>
      </div>
    </div>
  );
}

export function TasksHero() {
  return (
    <section className="relative overflow-x-clip border-b border-border bg-background pb-16 pt-28 sm:pb-20 lg:pt-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 lg:px-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,600px)] xl:gap-10">
        <HeroCopy />
        <Stage />
      </div>
    </section>
  );
}
